import {
  ESignatureVerificationTypeId,
  ESigningMethod,
  EWorkspaceDocumentTabs,
} from '@literax/enums/document.enum';
import {
  IAttachmentRequestParams,
  IViewingAttachment,
} from '@literax/models/attachment.model';
import {
  ILegalPerson,
  INaturalPerson,
  IObserver,
  ISignRequestQuote,
} from '@literax/models/participant.model';

import {
  IDocumentResponse,
  IVerificationType,
} from '@literax/models/document.model';
import {
  ILookFeelConfig,
  IAccessTokens,
} from '../../../../components/configurations/users/models/users.interface';
import { IReminder } from '@literax/models/reminder.model';
import { ELinkOutcome } from '@literax/models/link-outcome.model';

export interface IDocumentConfiguration {
  ordering: boolean;
  flowName: string;
  signingMethod: ESigningMethod;
  signatureVerificationTypeId: ESignatureVerificationTypeId;
  showBiometricsCheck: boolean;
  legalPersons: ILegalPerson[];
  naturalPersons: INaturalPerson[];
  participants: Array<INaturalPerson | ILegalPerson>;
  observers: IObserver[];
  participantsCount: number;
  observersCount: number;
  participantSignatureQuotes: ISignRequestQuote[];
}

export enum EWorkingMode {
  AUTH = 0,
  GUEST,
  INTEGRATOR,
}

export enum IDocxAttachmentState {
  IDLE = 0,
  GENERATING,
  GENERATED,
}

export interface WorkspaceState {
  document: IDocumentResponse | null;
  attachment: IViewingAttachment | null;
  mainAttachment: IViewingAttachment | null;
  viewerOptions: IAttachmentRequestParams | null;
  activeTab: EWorkspaceDocumentTabs;
  formBuilderRequiredFieldsFilled: boolean;
  isPromissoryNoteFlow: boolean;
  actionToDispatch: string | null;
  configuration: IDocumentConfiguration;
  errorMessage: string | null | any;
  /**
   * The resolved outcome of the guest-link request that failed, or `null`.
   *
   * Written only by the entry guard, which is the sole caller of the outcome resolution service
   * on this path; cleared whenever a document loads successfully, so a stale outcome cannot
   * mislabel a later completion. `null` means "not an outcome-driven arrival", which is how a
   * guest who has just finished reaches the completed screen (FR-030, FR-031, D-13).
   *
   * It travels beside `errorMessage`, never inside it: that value stays a plain message because
   * the rejection screen renders it directly as text.
   *
   * **Coupling to be aware of before adding anything to the `/signed` path.** The reducer assigns
   * `action.payload.outcome ?? null` on **every** `getDocumentError`, not only on the one the entry
   * guard dispatches. So a future dispatch of that action while the completed screen is open would
   * reset this field and silently revert the already-participated wording to the success wording —
   * the recipient would be told they had just finished something they had in fact finished earlier.
   * Nothing can do that today: `/signed` is a bare route with no guard, resolver or shell, and both
   * pre-existing dispatch sites live in `workspace.effects.ts` effects that only fire from workspace
   * components. It is recorded here so a later `/signed` resolver is not added blind (FR-030,
   * FR-031, D-13).
   */
  linkOutcome: ELinkOutcome | null;
  guestEmail: string | null;
  guestToken: string | null;
  lookFeel: ILookFeelConfig;
  workingModeType: EWorkingMode;
  tokens: IAccessTokens;
  docxAttachmentState: IDocxAttachmentState;
  signatureVerificationTypes: IVerificationType[];
}

export const initialWorkspaceState: WorkspaceState = {
  document: null,
  attachment: null,
  mainAttachment: null,
  viewerOptions: null,
  activeTab: EWorkspaceDocumentTabs.ATTACHMENTS,
  formBuilderRequiredFieldsFilled: false,
  isPromissoryNoteFlow: false,
  actionToDispatch: null,
  configuration: {
    ordering: false,
    flowName: null,
    signingMethod: ESigningMethod.DIGITAL_SIGNATURE,
    signatureVerificationTypeId: ESignatureVerificationTypeId.EMAIL,
    showBiometricsCheck: false,
    legalPersons: [],
    naturalPersons: [],
    participants: [],
    observers: [],
    participantsCount: 0,
    observersCount: 0,
    participantSignatureQuotes: []
  },
  errorMessage: null,
  linkOutcome: null,
  guestEmail: null,
  guestToken: null,
  lookFeel: null,
  workingModeType: EWorkingMode.AUTH,
  tokens: null,
  docxAttachmentState: IDocxAttachmentState.IDLE,
  signatureVerificationTypes: []
};
